"use client";
/**
 * Top-nav quick-switch for Display Profiles — flip Laptop ↔ External Screen in
 * one click. Hidden until you've created at least one profile (in Settings →
 * Pages → Display Profiles). Managing/creating still lives in Settings.
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/spa/Link";
import {
  getActiveProfileId, loadProfiles, PROFILE_APPLIED_EVENT, switchProfile, type DisplayProfile,
} from "@/lib/display/profiles";

export function ProfileSwitcher() {
  const [list, setList] = useState<DisplayProfile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const refresh = () => { setList(loadProfiles()); setActiveId(getActiveProfileId()); };
    refresh();
    window.addEventListener(PROFILE_APPLIED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_APPLIED_EVENT, refresh);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (list.length === 0) return null;
  const active = list.find((p) => p.id === activeId) ?? null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title="Display profile"
        className="flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs text-ink-muted transition hover:border-accent hover:text-ink"
      >
        {/* monitor glyph */}
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
        <span className="max-w-[9rem] truncate">{active ? active.name : "Layout"}</span>
        <svg className="h-3 w-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-56 rounded-lg border border-line bg-surface p-1 shadow-panel-raised">
          <div className="px-2 py-1 text-2xs uppercase tracking-wide text-ink-faint">Display profile</div>
          {list.map((p) => (
            <button
              key={p.id}
              onClick={() => { switchProfile(p); window.location.reload(); }}
              className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm transition hover:bg-accent-soft/40 ${p.id === activeId ? "text-accent" : "text-ink"}`}
            >
              <span className="truncate">{p.name}</span>
              {p.id === activeId && (
                <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              )}
            </button>
          ))}
          <Link
            href="/settings/pages"
            onClick={() => setOpen(false)}
            className="mt-1 block border-t border-line px-2 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Manage profiles…
          </Link>
        </div>
      )}
    </div>
  );
}
